import { FC, useCallback, useEffect, useMemo, useState } from "react";

import { IAccommodationBlockParams } from "Bands/types";
import { FormBuilder, PlacesInput } from "shared/components";
import { IAddress, IAPIError, IAPIState, IFormButtons } from "shared/types";

import { isValidTimestamp } from "Auth/utils/parseTimeField";
import { useBandRegionalSettings, useIsStartDateBeforeEndDate } from "Bands/utils";
import { useAccommodationBlockLocationOptions } from "Bands/utils/hooks/useAccommodationBlockLocationOptions";
import { useSuggestedLocation } from "Bands/utils/hooks/useSuggestedLocation";
import { isEmpty, isEqualWith } from "lodash-es";
import { API_ENDPOINTS } from "shared/constants";
import { getFormFields } from "./formData";

interface AccommodationBlockEntryFormProps {
  apiStatus?: IAPIState;
  error?: boolean | string;
  defaultValues?: IAccommodationBlockParams;
  buttons?: IFormButtons;
  apiError?: IAPIError | null;
  excludeUsers?: string[] | undefined;
  handleSubmit: (data: IAccommodationBlockParams) => void;
}

export const AccommodationBlockForm: FC<AccommodationBlockEntryFormProps> = ({
  defaultValues,
  apiStatus,
  error,
  buttons,
  handleSubmit,
  apiError,
  children,
}) => {
  const { dateFormat, timeFormat } = useBandRegionalSettings();

  const [accommodationName, setAccommodationName] = useState<string>(
    defaultValues?.accommodationName || ""
  );
  const [address, setAddress] = useState<IAddress | null>(defaultValues?.address || null);
  const [tempAddress, setTempAddress] = useState<IAddress | null>(defaultValues?.address || null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const [forceUpdateValues, setForceUpdateValues] = useState<
    IAccommodationBlockParams | undefined
  >();

  const startBeforeEndValues = useMemo(
    () => ({
      startDate: defaultValues?.checkInDate,
      startTime: defaultValues?.checkInTime,
      endDate: defaultValues?.checkOutDate,
      endTime: defaultValues?.checkOutTime,
    }),
    [defaultValues]
  );

  const { start, end, isStartBeforeEnd, handleStartDateChange, handleEndDateChange } =
    useIsStartDateBeforeEndDate({
      defaultValues: startBeforeEndValues,
      includeTime: false,
    });

  const handleBlurAddress = (address: IAddress | null) => {
    setTempAddress(address);
  };

  const submitAddress = useCallback((address: IAddress | null) => {
    setAddress(address);
  }, []);

  const preventSubmit = useMemo(() => !address, [address]);

  const { options, isFetchingLocations, fetchGoogleLocations } =
    useAccommodationBlockLocationOptions();

  const { data: selectedLocationData } = useSuggestedLocation(selectedLocationId);

  // Add location parameter in request body if address is unchanged
  const location = useMemo(() => {
    if (
      !isEqualWith(
        address,
        selectedLocationData?.address,
        (a, b) => a === b || (isEmpty(a) && isEmpty(b))
      )
    )
      return undefined;

    return API_ENDPOINTS.EVENT_SUGGESTED_LOCATION(selectedLocationData?.id || "");
  }, [selectedLocationData, address]);

  const onSubmit = (partialAccommodation: IAccommodationBlockParams) => {
    if (address) {
      if ((partialAccommodation?.amenities as unknown) === "") partialAccommodation.amenities = []; // Cast empty string to empty array.

      const accommodation: IAccommodationBlockParams = {
        ...partialAccommodation,
        accommodationName: accommodationName || "",
        checkInTime: isValidTimestamp(start?.time) ? start?.time : "",
        checkOutTime: isValidTimestamp(end?.time) ? end?.time : "",
        address,
        location,
      };

      handleSubmit(accommodation);
    }
  };

  // Map selectedLocationData.address to IAddress format and prefill form
  useEffect(() => {
    if (selectedLocationData?.address) {
      setAddress(selectedLocationData?.address as IAddress);
      setTempAddress(selectedLocationData?.address as IAddress);
      submitAddress(selectedLocationData?.address as IAddress);
    }
  }, [selectedLocationData, submitAddress]);

  const fields = useMemo(
    () =>
      getFormFields({
        isStartBeforeEnd,
        handleStartDateChange,
        handleEndDateChange,
        dateFormat,
        timeFormat,
        submitAddress,
        defaultValues: {
          ...defaultValues,
          address: (address || tempAddress) as IAddress | undefined,
        },
        handleBlurAddress,
        address,
        setAddress,
        tempAddress,
        setTempAddress,
      }),
    [
      dateFormat,
      defaultValues,
      handleEndDateChange,
      handleStartDateChange,
      isStartBeforeEnd,
      timeFormat,
      address,
      tempAddress,
      submitAddress,
      setTempAddress,
    ]
  );

  // if start date changes, update end date to the future
  useEffect(() => {
    if (start?.date && end?.date && !defaultValues?.address) {
      const startDateCompare = new Date(start?.date);
      const endDateCompare = new Date(end?.date);

      if (startDateCompare > endDateCompare) {
        const forcedEndDate = new Date(start.date);
        forcedEndDate.setDate(forcedEndDate.getDate() + 1);

        setForceUpdateValues({
          checkOutDate: forcedEndDate.toISOString().split("T")[0],
        });

        handleEndDateChange({
          ...end,
          date: forcedEndDate.toISOString().split("T")[0],
        });
      }
    }
  }, [start?.date, end?.date, handleEndDateChange, end, defaultValues?.address]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      fetchGoogleLocations(accommodationName);
    }, 200);

    return () => clearTimeout(debounceTimer);
  }, [accommodationName, fetchGoogleLocations]);

  return (
    <>
      <PlacesInput
        name="accommodationName"
        label="Accommodation Name"
        options={options}
        onOptionSelect={option => setSelectedLocationId(option.value)}
        value={accommodationName}
        onChange={setAccommodationName}
        isLoading={isFetchingLocations}
      />
      <FormBuilder
        isSubmitting={apiStatus === IAPIState.pending}
        apiErrorMessage={error}
        fields={fields}
        onSubmit={onSubmit}
        buttons={buttons}
        defaultValues={defaultValues}
        apiError={apiError}
        preventSubmit={preventSubmit}
        showSaveChanges={true}
        forceUpdateValues={forceUpdateValues}
      />
      {children}
    </>
  );
};
